/**
 * App shell: rail | (top bar / warnings / outlet) | side panel.
 * Also owns the SSE subscription (follows the URL `live` flag), the global
 * keyboard shortcuts from spec §7, and the app-wide singletons (shortcut
 * help, the jump palette, the notes composer dialog, the alert toasts).
 */
import { useLayoutEffect } from "react";
import { Outlet } from "react-router";
import { useCurrentJob } from "@/state/job";
import { useAxisFrames } from "@/views/overview_tape/lib/axisSync";
import { useOverviewStore } from "@/views/overview_tape/store";
import { useSse } from "@/api/sse";
import { CommandPalette } from "@/components/CommandPalette";
import { KeyboardShortcutsListener, ShortcutsHelp } from "@/components/KeyboardShortcuts";
import { useShortcuts } from "@/lib/shortcuts";
import { useUrlState } from "@/state/useUrlState";
import { useUi } from "@/state/ui";
import { NoteComposer } from "@/views/family/notes/NoteComposer";
import { AlertToasts } from "@/views/alerts/AlertToasts";
import { Toasts } from "@/components/Toasts";
import { LeftRail } from "./LeftRail";
import { SidePanel } from "./SidePanel";
import { TopBar } from "./TopBar";
import { WarningsBar } from "./WarningsBar";

export function AppShell() {
  const { id } = useCurrentJob();
  useLayoutEffect(() => {
    useOverviewStore.getState().resetJobState();
    useAxisFrames.setState({ frames: {} });
    useUi.getState().resetJobState();
  }, [id]);
  const { state, setLive, nudge, zoom, setFilters } = useUrlState();
  useSse(state.live);
  const toggleSide = useUi((s) => s.toggleSidePanel);
  useShortcuts([
    { key: "[", description: "nudge range earlier", group: "time", handler: () => nudge(-0.5) },
    { key: "]", description: "nudge range later", group: "time", handler: () => nudge(0.5) },
    { key: "+", description: "zoom in", group: "time", handler: () => zoom(0.5) },
    { key: "=", description: "zoom in", group: "time", handler: () => zoom(0.5) },
    { key: "-", description: "zoom out", group: "time", handler: () => zoom(2) },
    {
      key: "f",
      description: "toggle flagged-only filter",
      group: "filters",
      handler: () => setFilters({ flagged: state.filters.flagged ? undefined : true }),
    },
    { key: "l", description: "toggle live mode", group: "general", handler: () => setLive(!state.live) },
    { key: "p", description: "toggle side panel", group: "general", handler: toggleSide },
  ]);
  return (
    <div className="flex h-full w-full overflow-hidden">
      <KeyboardShortcutsListener />
      <ShortcutsHelp />
      <CommandPalette />
      <NoteComposer />
      <AlertToasts />
      <Toasts />
      <LeftRail />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar />
        <WarningsBar />
        <main className="min-h-0 flex-1 overflow-auto bg-bg">
          <Outlet key={id ?? "global"} />
        </main>
      </div>
      <SidePanel />
    </div>
  );
}
